import { Box } from '@material-ui/core';
import {
  getLoanById,
  Loan,
  OnboardingDetailsRole,
  OnboardingDetailsType,
  patchSetBorrowerOnboardingDetailsByRole,
  Relation,
} from '@nemuru-eng/js-api-client';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { RelationOptions } from '@/components/modules/KYC/KYCLoan/shared/BorrowerInvitationSection/constants/RelationOptions';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { PhoneFormDoubleField } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/PhoneFormDoubleField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import {
  AddParticipantToLoanSchema,
  AddParticipantToLoanType,
} from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/schemas/AddParticipantToLoanSchema';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { removeNullKeysFromObject } from '@/utils/removeNullKeysFromObject';

interface AddParticipantToLoanDialogProps {
  loan: Loan;
  opened: boolean;
  setOpened: (boolean) => void;
  handleClose;
}

export const AddParticipantToLoanDialog = ({
  loan,
  opened,
  setOpened,
  handleClose,
}: AddParticipantToLoanDialogProps) => {
  const { revalidate } = useCache(getLoanById, { loanId: loan.id });
  const { request, loading } = useRequest();

  const watch = useFormContext()?.watch;
  const loanIsOfTypeBusiness = loan.onboardingDetails.some(
    (details) => details.type === OnboardingDetailsType.BUSINESS,
  );
  const participantRoleWatcher: string = watch ? watch('participantRole') : undefined;

  const mainButtonTextId = participantRoleWatcher && `add_${participantRoleWatcher}`;

  const addParticipantToLoan = async (values: AddParticipantToLoanType) => {
    const requestBody = {
      id: loan.id,
      role: loanIsOfTypeBusiness ? OnboardingDetailsRole.GUARANTOR : values.participantRole,
      borrowerOnboardingDetailsType: OnboardingDetailsType.BORROWER,
      borrowerOnboardingDetailsId: uuidv4(),
      invitationEmail: values.invitationEmail,
      invitationFirstName: values.invitationFirstName,
      invitationLastName: values.invitationLastName,
      invitationPhoneNumber: `+${values.dialCode.dialCode}${values.phoneNumber}`,
      invitationRelation: loanIsOfTypeBusiness ? Relation.OTHER : values.invitationRelation,
      invitationDocumentNumber: values.invitationDocumentNumber,
    };
    const formattedRequestBody = removeNullKeysFromObject(requestBody);
    await request(patchSetBorrowerOnboardingDetailsByRole, formattedRequestBody);
    revalidate();
    setOpened(false);
  };

  const rolesToAddToLoan = Object.values(OnboardingDetailsRole)
    .filter(
      (role) =>
        role === OnboardingDetailsRole.GUARANTOR ||
        (!loan.onboardingDetails.some((detail) => detail.role === OnboardingDetailsRole.CO_BORROWER) &&
          role === OnboardingDetailsRole.CO_BORROWER),
    )
    .map((value) => ({
      value: value,
      display: <Text tId={value} />,
    }));

  return (
    <EditDialogWrapper
      title={'ADD_PARTICIPANT_DIALOG'}
      saveButtonTextId={mainButtonTextId}
      isLoading={loading}
      onSubmitForm={addParticipantToLoan}
      opened={opened}
      setOpened={setOpened}
      handleClose={handleClose}
      formSchema={AddParticipantToLoanSchema}
    >
      <>
        <Font variant={'subtitle2'} color={'textSecondary'}>
          <Text tId={'ADD_PARTICIPANT_DIALOG_TEXT'} />
        </Font>
        <Box pt={3}>
          <FormGridContainer fieldVariant={FieldVariant.form} fullWidth>
            <SelectFormField name={'participantRole'} label={'PARTICIPANT_ROLE'} selectOptions={rolesToAddToLoan} />
            <TextFormField name={'invitationFirstName'} label={'FIRST_NAME'} />
            <TextFormField name={'invitationLastName'} label={'LAST_NAME'} />
            <PhoneFormDoubleField fieldVariant={FieldVariant.form} />
            <TextFormField name={'invitationEmail'} label={'EMAIL'} />
            <SelectFormField
              name={'invitationRelation'}
              label={'PARTICIPANT_RELATION'}
              selectOptions={RelationOptions}
            />
            <TextFormField name={'invitationDocumentNumber'} label={'DOCUMENT_NUMBER'} />
          </FormGridContainer>
        </Box>
      </>
    </EditDialogWrapper>
  );
};
